import { useNavigate, useSearch } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import { usePageHeader } from '@/components/page-header-context'
import { Pill } from '@/components/ui/pill'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useExportRides, useRides } from '@/features/rides/use-rides'
import {
  PAGE_SIZES,
  STATUS_META,
  STATUS_OPTIONS,
  type RidesFilters,
  type StatusFilter,
} from '@/features/rides/rides.types'

const COLS = 'grid grid-cols-[1.4fr_1.6fr_1.6fr_1.1fr_0.9fr_0.9fr_1.1fr] gap-4'

/** Lit les filtres bruts de l'URL et applique les valeurs par défaut. */
function parseFilters(search: Record<string, unknown>): RidesFilters {
  const page = Number(search.page)
  const limit = Number(search.limit)
  return {
    status: (search.status as StatusFilter) || undefined,
    from: (search.from as string) || undefined,
    to: (search.to as string) || undefined,
    driverId: (search.driverId as string) || undefined,
    passengerId: (search.passengerId as string) || undefined,
    page: Number.isFinite(page) && page >= 1 ? page : 1,
    limit: (PAGE_SIZES as readonly number[]).includes(limit) ? limit : 25,
  }
}

function fmtPrice(value: number): string {
  return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DT`
}

export default function RidesPage() {
  usePageHeader('Courses', 'Historique complet des courses avec filtres et export')
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as Record<string, unknown>
  const filters = parseFilters(search)

  const { data, isLoading, isError, isFetching } = useRides(filters)
  const exportCsv = useExportRides()
  const rides = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / filters.limit))

  /** Met à jour l'URL (et donc les filtres). Tout changement de filtre repart page 1. */
  const setFilters = (patch: Partial<RidesFilters>) => {
    navigate({
      to: '/rides',
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, ...patch }
        if (!('page' in patch)) next.page = 1
        // Nettoie les clés vides pour garder une URL lisible/partageable.
        for (const key of Object.keys(next)) {
          if (next[key] === '' || next[key] === undefined) delete next[key]
        }
        return next
      },
    })
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Barre de filtres */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-bold uppercase tracking-wide text-muted">Statut</span>
          <select
            aria-label="Filtrer par statut"
            value={filters.status ?? ''}
            onChange={(e) => setFilters({ status: (e.target.value || undefined) as StatusFilter })}
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Tous</option>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-bold uppercase tracking-wide text-muted">Du</span>
          <Input
            type="date"
            aria-label="Date de début"
            className="h-9 w-auto"
            value={filters.from?.slice(0, 10) ?? ''}
            onChange={(e) => setFilters({ from: e.target.value || undefined })}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-bold uppercase tracking-wide text-muted">Au</span>
          <Input
            type="date"
            aria-label="Date de fin"
            className="h-9 w-auto"
            value={filters.to?.slice(0, 10) ?? ''}
            onChange={(e) => setFilters({ to: e.target.value || undefined })}
          />
        </label>

        <label className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <span className="text-[11px] font-bold uppercase tracking-wide text-muted">
            Chauffeur (UUID)
          </span>
          <Input
            aria-label="Filtrer par chauffeur"
            placeholder="driverId…"
            className="h-9"
            defaultValue={filters.driverId ?? ''}
            onBlur={(e) => setFilters({ driverId: e.target.value.trim() || undefined })}
          />
        </label>

        <Button
          type="button"
          variant="outline"
          className="h-9 gap-2"
          disabled={exportCsv.isPending || total === 0}
          onClick={() => exportCsv.mutate(filters)}
        >
          <Download size={15} strokeWidth={2.2} />
          {exportCsv.isPending ? 'Export…' : 'Exporter CSV'}
        </Button>
      </div>

      {exportCsv.isError && (
        <p className="mt-2 text-xs text-danger">Échec de l'export — réessayez.</p>
      )}

      {/* Tableau */}
      <div className="mt-4 bg-surface rounded-[18px] border border-line overflow-hidden flex-1 flex flex-col min-h-0">
        <div className={`${COLS} px-6 py-3.5 border-b border-line bg-surfaceAlt`}>
          {['Course', 'Passager', 'Chauffeur', 'Statut', 'Distance', 'Prix', 'Date'].map((h) => (
            <span key={h} className="text-[11px] font-bold tracking-wide uppercase text-muted">
              {h}
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading && <p className="p-6 text-muted">Chargement…</p>}
          {isError && <p className="p-6 text-danger">Erreur lors du chargement des courses.</p>}
          {!isLoading && !isError && rides.length === 0 && (
            <p className="p-8 text-center text-muted">Aucune course trouvée</p>
          )}
          {!isLoading &&
            !isError &&
            rides.map((r) => {
              const meta = STATUS_META[r.status]
              return (
                <div
                  key={r.id}
                  className={`${COLS} items-center px-6 py-3.5 border-b border-line last:border-0`}
                >
                  <span className="font-mono text-[12px] text-ink-soft">{r.id.slice(0, 8)}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink truncate">{r.passengerName}</div>
                    <div className="font-mono text-[11.5px] text-muted">
                      {r.passengerPhone ?? '—'}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink truncate">
                      {r.driverName ?? '—'}
                    </div>
                    <div className="font-mono text-[11.5px] text-muted">{r.driverPhone ?? '—'}</div>
                  </div>
                  <div>
                    <Pill tone={meta.tone} className="!px-2.5 !py-1 text-[11px]">
                      {meta.label}
                    </Pill>
                  </div>
                  <span className="text-[13px] text-ink-soft">
                    {r.estimatedDistanceKm.toLocaleString('fr-FR')} km
                  </span>
                  <span className="text-[13px] font-semibold text-ink">{fmtPrice(r.price)}</span>
                  <span className="text-[13px] text-ink-soft">
                    {new Date(r.createdAt).toLocaleDateString('fr-TN')}
                  </span>
                </div>
              )
            })}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between gap-4 px-6 py-3 border-t border-line bg-surfaceAlt">
          <div className="flex items-center gap-3">
            <span className="text-[12.5px] text-muted">
              {total} course{total > 1 ? 's' : ''}
              {isFetching && ' · …'}
            </span>
            <select
              aria-label="Lignes par page"
              value={filters.limit}
              onChange={(e) => setFilters({ limit: Number(e.target.value) })}
              className="h-8 rounded-lg border border-gray-300 px-2 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={filters.page <= 1}
              onClick={() => setFilters({ page: filters.page - 1 })}
            >
              Précédent
            </Button>
            <span className="text-[12.5px] text-ink-soft">
              {filters.page} / {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={filters.page >= totalPages}
              onClick={() => setFilters({ page: filters.page + 1 })}
            >
              Suivant
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
